import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, lifecycle, onlyUpdateForKeys, withProps } from 'recompose';
import { translate, TranslationFunction } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { ZoneSelection } from './ZoneSelection';
import { Button } from 'ui/shared/Button';
import { HeatMapZone } from 'logic/reports/HeatMapZone';
import { ChangeImageDialog } from 'ui/pages/changeImage/ChangeImageDialog';
import { DeviceIndex, DeviceIdMap, UtcTime } from 'types';
import { RenameZoneDialog } from 'ui/pages/designer/sections/zoneSelection/RenameZoneDialog';
import { postSeekRequest } from 'api/camera';
import { State } from 'reducers';
import { connect } from 'react-redux';
import { getDeviceIdByDeviceIndex } from 'selectors/deviceSelectors';
import { Required } from 'ui/pages/designer/Required';
import { Fade } from 'ui/transitions';
import { Loading } from 'ui/shared/Loading';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const styles: { [id: string]: React.CSSProperties } = {
    wrapper: {
        display: 'inline-block',
    },
    buttonBar: {
        position: 'relative',
        marginBottom: '.5em',
    },
    changeImage: {
        position: 'absolute',
        right: 0,
    },
    zoneSelectionHost: {
        position: 'relative',
        width: '60em',
        height: '40em',
    },
};

const RawZoneSelectionSection = ({
    isLoadingImage,
    zones,
    base64image,
    selectedZoneName,
    deviceIndex,
    renamingZoneVisible,
    changeImageVisible,
    handleClickAddZone,
    handleClickRenameZone,
    handleClickRemoveZone,
    handleClickChangeImage,
    handleRenameZone,
    handleCancelRenameZone,
    handleSelectZone,
    handleChangeZone,
    handleChangeImage,
    handleCancelChangeImage,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <div style={ styles.wrapper }>
        <div style={ styles.buttonBar } className="inline-child-divs">
            <Button
                id="add-zone"
                textKey="6.1241.0.65"
                onClick={ handleClickAddZone }
            />
            <Button
                id="rename-zone"
                textKey="WC_REPORT_HEAT_MAP_RENAME_ZONE"
                onClick={ handleClickRenameZone }
                disabled={ !selectedZoneName }
            />
            <Button
                id="remove-zone"
                textKey="6.1241.0.66"
                onClick={ handleClickRemoveZone }
                disabled={ !selectedZoneName }
            />
            <Required visible={ zones.length === 0 } />
            { deviceIndex &&
                <Button
                    id="zone-change-image"
                    textKey="Reports_Settings_SelectImage"
                    style={ styles.changeImage }
                    onClick={ handleClickChangeImage }
                    disabled={ !deviceIndex }
                />
            }
        </div>
        <div style={ styles.zoneSelectionHost }>
            <ZoneSelection
                zones={ zones }
                selectedZoneName={ selectedZoneName }
                image={ base64image }
                onChange={ handleChangeZone }
                onSelect={ handleSelectZone }
                onClickZoneName={ handleClickRenameZone }
            />
            <Fade
                visible={ isLoadingImage }
                duration={ 400 }
                unmountOnExit={ true }
            >
                <Loading textKey="6.24407.4553" size="small"/>
            </Fade>
        </div>

        <RenameZoneDialog
            visible={ renamingZoneVisible }
            name={ selectedZoneName }
            onAccept={ handleRenameZone }
            onCancel={ handleCancelRenameZone }
        />

        { deviceIndex &&
            <ChangeImageDialog
                visible={ changeImageVisible }
                deviceIndex={ deviceIndex }
                onAccept={ handleChangeImage }
                onCancel={ handleCancelChangeImage }
            />
        }
    </div>
;

type StateProps = {
    deviceIdByDeviceIndex: DeviceIdMap;
};
type RecomposeProps = {
    isLoadingImage: boolean;
    zones: ReadonlyArray<HeatMapZone>;
    base64image: string | null;
    selectedZoneName: string | null;
    deviceIndex: DeviceIndex | null;
    renamingZoneVisible: boolean;
    changeImageVisible: boolean;
    handleClickAddZone: () => void;
    handleClickRenameZone: () => void;
    handleClickRemoveZone: () => void;
    handleClickChangeImage: () => void;
    handleChangeZone: (name: string, z: HeatMapZone) => void;
    handleSelectZone: (name: string | null) => void;
    handleRenameZone: (newName: string) => void;
    handleCancelRenameZone: () => void;
    handleChangeImage: (image: string, utcTime: number) => Promise<void>;
    handleCancelChangeImage: () => void;
    handleLoadImage: () => Promise<void>;
};
type _RecomposeProps = RecomposeProps & {
    _setZones: (z: ReadonlyArray<HeatMapZone>) => void;
    _setSelectedZoneName: (n: string | null) => void;
    _setIsLoadingImage: (b: boolean) => void;
    _setRenamingZoneVisible: (v: boolean) => void;
    _setChangeImageVisible: (b: boolean) => void;
};

export const calculateNextZoneName = (zones: ReadonlyArray<HeatMapZone>, t: TranslationFunction): string => {
    let i = 1;
    while (true) {
        const name = t('WC_REPORT_HEAT_MAP_ZONE', [ i ]);
        const regex = new RegExp(`^${name}\\b`, 'i');
        if (zones.filter(z => regex.test(z.name)).length === 0) {
            return name;
        }
        i++;
    }
};

const mapStateToProps = (state: State) => ({
    deviceIdByDeviceIndex: getDeviceIdByDeviceIndex(state),
});

const loadImage = async (props: OwnProps & StateProps & _RecomposeProps & TranslateProps, utcTime: UtcTime) => {
    const { onChange, deviceIndex, deviceIdByDeviceIndex, _setIsLoadingImage } = props;

    const deviceId = deviceIdByDeviceIndex[`${deviceIndex}`];
    if (!deviceId) {
        // No device, so wipe out the heat map image
        onChange(reportSettings => reportSettings.setHeatMapImage(null));
        return;
    }

    _setIsLoadingImage(true);
    try {
        const { frame } = await postSeekRequest(deviceId, 0, 'to', utcTime, false);
        onChange(reportSettings => {
            // Make sure the camera didn't change while we were waiting for the frame to download
            const currentDeviceIndex = reportSettings.devices.map(d => d.deviceIndex).shift();
            if (deviceIndex === currentDeviceIndex) {
                reportSettings = reportSettings.setHeatMapImage(frame);
            }
            return reportSettings;
        });
    }
    catch (err) {
        console.error(err);
    }
    finally {
        _setIsLoadingImage(false);
    }
};


export const ZoneSelectionSection = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('ZoneSelectionSection'),
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withProps(({ reportSettings }: OwnProps) => ({
        deviceIndex: reportSettings.devices.map(d => d.deviceIndex).shift() || null,
        base64image: reportSettings.heatMapImage,
    })),
    withState('zones', '_setZones', ({ reportSettings }: OwnProps) => reportSettings.heatMapZones),
    withState('isLoadingImage', '_setIsLoadingImage', false),
    withState('selectedZoneName', '_setSelectedZoneName', null),
    withState('renamingZoneVisible', '_setRenamingZoneVisible', false),
    withState('changeImageVisible', '_setChangeImageVisible', false),
    withHandlers<OwnProps & _RecomposeProps & TranslateProps, Partial<RecomposeProps>>({
        handleChangeZone: ({ onChange, _setZones, _setSelectedZoneName }) => (name: string, zone: HeatMapZone) => {
            // This is called when a zone is moved/resized/renamed

            onChange(reportSettings => {
                const newReportSettings =
                    reportSettings
                        .removeHeatMapZone(name)
                        .addHeatMapZone(zone);

                _setZones(newReportSettings.heatMapZones);

                // Update the selected name if the zone was renamed
                if (name !== zone.name) {
                    _setSelectedZoneName(zone.name);
                }
    
                return newReportSettings;
            });
        },
    }),
    withHandlers<OwnProps & StateProps & _RecomposeProps & TranslateProps, Partial<RecomposeProps>>({
        handleClickAddZone: ({ onChange, _setSelectedZoneName, zones, t }) => () => {
            const uniqueName = calculateNextZoneName(zones, t);
            const newZone = new HeatMapZone(uniqueName, [
                { x: 0, y: 0 },
                { x: 100, y: 0 },
                { x: 0, y: 100 },
                { x: 100, y: 100 },
            ]);
            onChange(reportSettings => reportSettings.addHeatMapZone(newZone));
            _setSelectedZoneName(uniqueName);
        },
        handleClickRenameZone: ({ _setRenamingZoneVisible }) => () => {
            _setRenamingZoneVisible(true);
        },
        handleRenameZone: ({ handleChangeZone, zones, selectedZoneName, _setRenamingZoneVisible }) => (newName: string) => {
            const zone = zones.filter(z => z.name === selectedZoneName).shift();
            if (zone) {
                handleChangeZone(selectedZoneName!, zone.rename(newName));
            }
            _setRenamingZoneVisible(false);
        },
        handleCancelRenameZone: ({ _setRenamingZoneVisible }) => () => {
            _setRenamingZoneVisible(false);
        },
        handleClickRemoveZone: ({ onChange, selectedZoneName, _setSelectedZoneName }) => () => {
            onChange(reportSettings => reportSettings.removeHeatMapZone(selectedZoneName!));
            _setSelectedZoneName(null);
        },
        handleClickChangeImage: ({ _setChangeImageVisible }) => () => {
            _setChangeImageVisible(true);
        },
        handleChangeImage: props => async (image: string, utcTime: number) => {
            props._setChangeImageVisible(false);
            await loadImage(props, utcTime);
        },
        handleCancelChangeImage: ({ _setChangeImageVisible }) => () => {
            _setChangeImageVisible(false);
        },
        handleSelectZone: ({ _setSelectedZoneName }) => _setSelectedZoneName,
        handleLoadImage: props => async () => {
            await loadImage(props, 0);
        },
    }),
    lifecycle<OwnProps & _RecomposeProps, OwnProps>({
        componentDidMount() {
            // Kick off an image request if we don't have one in the report yet
            const { base64image, handleLoadImage } = this.props;
            if (!base64image) {
                handleLoadImage();
            }
        },
        componentDidUpdate(oldProps: OwnProps & _RecomposeProps) {
            const { reportSettings, deviceIndex, _setZones, handleLoadImage } = this.props;

            // Re-initialize our state if the incoming report settings have different number of
            // zones (i.e. the user added or removed a zone).
            const zones = reportSettings.heatMapZones;
            if (JSON.stringify(oldProps.zones) !== JSON.stringify(zones)) {
                _setZones(zones);
            }

            // Kick off an image request if the camera is changing
            if (oldProps.deviceIndex !== deviceIndex) {
                handleLoadImage();
            }
        },
    }),
    onlyUpdateForKeys([ 'isLoadingImage', 'zones', 'base64image', 'selectedZoneName', 'renamingZoneVisible', 'changeImageVisible', 'deviceIndex' ]),
)(RawZoneSelectionSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/zoneSelection/ZoneSelectionSection.tsx